import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import { MOMENT_KEY, STORAGE_KEY, STORAGE_KEY_REFRESH } from "../utils/config";
import {
  type LoginInfo,
  type RefreshData,
  validateUserData,
  hasUserPermission,
  normalizeUserData,
  isValidLoginInfo
} from "../models/login";
import { seOtelLogstUserAttributes } from "../services/otel/instrumentation";
import analytics from "../utils/analytics";
import { datadogRum } from '@datadog/browser-rum';
import { decodeBase64, STATUSES } from "../../src/common/status";


// ==================== INTERFACES ====================

/**
 * Token expiration state interface
 */
interface TokenExp {
  ssoToken: boolean;
  checked?: boolean;
}

/**
 * Login event payload interface
 */
interface LoginEventPayload {
  email: string | null;
  mobile: string | null;
  identifier?: string;
}

/**
 * OTP data payload interface
 */
interface OtpDataPayload {
  data: any;
  user_id: string | null;
}

/**
 * Email and mobile payload interface
 */
interface EmailMobilePayload {
  email: string | null;
  mobile: string | null;
}

/**
 * QR data payload interface
 */
interface QrDataPayload {
  data: any;
}

/**
 * Auth token update payload interface
 */
interface AuthTokenUpdatePayload {
  data: string;
}

/**
 * Complete auth state interface
 */
interface AuthState {
  user: LoginInfo | null;
  isNewUser: boolean;
  isLoggedIn: boolean;
  status: string;
  error_message: string | null;
  email: string | null;
  mobile: string | null;
  data: RefreshData | any | null; // Can be RefreshData or other data types
  user_id: string | null;
  themeData: any; // Consider creating a ThemeData interface
  theamDataBG: any; // Consider creating a ThemeDataBG interface
  tokenExp: TokenExp;
  sessionTime: number;
  isOTPVerified: boolean; // for TOTP and OTP verification
  identifier?: string;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Safely parse JSON from localStorage
 */
function safeParseJSON<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined') return fallback;
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  } catch (error) {
    console.error(`Failed to parse ${key} from localStorage:`, error);
    return fallback;
  }
}

/**
 * Check if SSO token is valid and not expired
 */
function checkTokenValid(state: AuthState): void {
  const ssoToken = state.user?.ssoToken;

  if (ssoToken) {
    try {
      const tokenParts = ssoToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const decodedPayload = JSON.parse(decodeBase64(tokenParts[1]!));

      // Update application ID in data
      state.data = {
        ...state.data,
        applicationId: decodedPayload.applicationId
      };

      // Check token expiration
      const expirationDate = new Date(0);
      expirationDate.setUTCSeconds(decodedPayload.exp);
      const currentTime = moment();
      const expirationTime = moment(expirationDate);
      const isExpired = currentTime.isAfter(expirationTime);
      const diffInMinutes = currentTime.diff(expirationTime, 'minutes');

      if (diffInMinutes > 0 || isExpired) {
        console.warn('SSO Token has expired');
        state.tokenExp = { ...state.tokenExp, ssoToken: true };
      } else {
        state.tokenExp = { ...state.tokenExp, ssoToken: false, checked: true };
      }
    } catch (error) {
      console.error('Error checking token validity:', error);
      state.tokenExp = { ...state.tokenExp, ssoToken: true };
    }
  }
}

/**
 * Persist user data to localStorage
 */
function persistUserData(userData: LoginInfo): void {
  try {
    if (typeof window === 'undefined') return;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));

    if (userData.access_token) {
      localStorage.setItem('dock_universal_token', userData.access_token);
    }

    if (userData.user_id) {
      localStorage.setItem('user_id', userData.user_id);
    }
  } catch (error) {
    console.error('Failed to persist user data:', error);
  }
}

/**
 * Persist session time to localStorage
 */
function persistSessionTime(sessionTime: number): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(MOMENT_KEY, JSON.stringify(sessionTime));
  } catch (error) {
    console.error('Failed to persist session time:', error);
  }
}

/**
 * Persist refresh data to localStorage
 */
function persistRefreshData(refreshData: any): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY_REFRESH, JSON.stringify(refreshData));
  } catch (error) {
    console.error('Failed to persist refresh data:', error);
  }
}

/**
 * Set user attributes for external services
 */
function setExternalUserAttributes(userData: LoginInfo): void {
  try {
    // Set OTEL user attributes
    seOtelLogstUserAttributes({
      clientCode: userData.user_id,
    });

    // Set analytics user identification
    if (userData.mobile && userData.mobile.length > 0) {
      analytics.identify(userData.mobile, {
        email: userData.email,
        mobile: userData.mobile,
        name: userData.user_name,
        client_code: userData.user_id,
      });
    }

    // Set Datadog RUM user
    datadogRum.setUser({
      id: userData.user_id,
      name: userData.user_name,
      email: userData.email,
    });
  } catch (error) {
    console.error('Failed to set external user attributes:', error);
  }
}

/**
 * Clear all auth-related localStorage
 */
function clearAuthStorage(): void {
  if (typeof window === 'undefined') return;

  const keysToRemove = [
    STORAGE_KEY,
    STORAGE_KEY_REFRESH,
    MOMENT_KEY,
    'dock_universal_token',
    'user_id',
    'user_client_code'
  ];

  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key} from localStorage:`, error);
    }
  });
}

/**
 * Send GTM events safely
 */
function sendGTMEvent(eventData: any): void {
  try {
    if (typeof window !== 'undefined' && (window as any).GtmPusher) {
      (window as any).GtmPusher(eventData);
    }
  } catch (error) {
    console.error('Failed to send GTM event:', error);
  }
}

// ==================== INITIAL STATE ====================

/**
 * SSR-safe initial state setup
 */
let initialState: AuthState = {
  user: null,
  isNewUser: false,
  isLoggedIn: false,
  status: STATUSES.IDLE,
  error_message: null,
  email: null,
  mobile: null,
  data: null,
  user_id: null,
  themeData: null,
  theamDataBG: null,
  tokenExp: { ssoToken: false },
  sessionTime: 0,
  isOTPVerified: false
};

// Initialize from localStorage if in browser environment
if (typeof window !== 'undefined') {
  const storedUser = safeParseJSON<LoginInfo | null>(STORAGE_KEY, null);
  const storedRefreshData = safeParseJSON<RefreshData | null>(STORAGE_KEY_REFRESH, null);
  const storedSessionTime = safeParseJSON<number>(MOMENT_KEY, 0);

  initialState = {
    user: storedUser,
    isNewUser: false,
    isLoggedIn: !!storedUser,
    status: STATUSES.IDLE,
    error_message: null,
    email: storedUser?.email || null,
    mobile: storedUser?.mobile || null,
    data: storedRefreshData,
    user_id: storedUser?.user_id || null,
    themeData: null,
    theamDataBG: null,
    tokenExp: { ssoToken: false },
    sessionTime: Number(storedSessionTime),
    isOTPVerified: false
  };
}

// ==================== SLICE DEFINITION ====================

const authReducer = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<LoginInfo>) {
      const userData = action.payload;

      // Validate user data
      const validation = validateUserData(userData);
      if (!validation.isValid) {
        console.warn('Invalid user data:', validation.errors);
        state.error_message = 'Invalid user data: ' + validation.errors.join(', ');
        return;
      }

      // Set external user attributes
      setExternalUserAttributes(userData);

      // Update state
      state.user = userData;
      state.isLoggedIn = true;
      state.email = userData.email || null;
      state.mobile = userData.mobile || null;
      state.user_id = userData.user_id || null;
      state.error_message = null;

      // Persist to localStorage
      persistUserData(userData);
    },

    setStatus(state, action: PayloadAction<string>) {
      state.status = action.payload;
    },

    setMessage(state, action: PayloadAction<string>) {
      state.error_message = action.payload;
    },

    clearMessage(state) {
      state.error_message = null;
    },

    logout(state) {
      state.isLoggedIn = false;
      state.user = null;
      state.email = null;
      state.mobile = null;
      state.user_id = null;
      state.data = null;
      state.sessionTime = 0;
      state.tokenExp = { ssoToken: false };
      state.isOTPVerified = false;
      state.isNewUser = false;
      state.error_message = null;

      // Clear localStorage
      clearAuthStorage();
    },

    setMpinEnable(state) {
      if (state.user) {
        state.user.mpin_enabled = true;
        state.user.is_mpin_set = true;
        // Persist updated user data
        persistUserData(state.user);
      }
    },

    setEmailAndMobile(state, action: PayloadAction<EmailMobilePayload>) {
      state.email = action.payload.email;
      state.mobile = action.payload.mobile;
    },

    setQrData(state, action: PayloadAction<QrDataPayload>) {
      state.data = action.payload.data;
      persistRefreshData(state.data);
    },

    setOtpData(state, action: PayloadAction<OtpDataPayload>) {
      state.data = action.payload.data;
      state.user_id = action.payload.user_id;
      persistRefreshData(state.data);
    },

    updateAuthTokenForSession(state, action: PayloadAction<AuthTokenUpdatePayload>) {
      if (state.data) {
        state.data = {
          ...state.data,
          token: action.payload.data
        };
        persistRefreshData(state.data);
      }
    },

    setSessionTime(state, action: PayloadAction<number>) {
      state.sessionTime = action.payload;
      persistSessionTime(action.payload);
    },

    setThemeData(state, action: PayloadAction<any>) {
      state.themeData = action.payload;
    },

    setThemeDataBG(state, action: PayloadAction<any>) {
      state.theamDataBG = action.payload;
    },

    initialCheckData(state) {
      checkTokenValid(state);
    },

    loginEvent(state, action: PayloadAction<LoginEventPayload>) {
      const { email, mobile, identifier } = action.payload;

      if (email && mobile) {
        sendGTMEvent({
          event: 'tv-login',
          email: email,
          mobile_number: mobile,
          identifier: identifier
        });
      } else {
        // Fallback to state values if payload is empty
        sendGTMEvent({
          event: 'tv-login',
          email: state.email,
          mobile_number: state.mobile,
          identifier: state.identifier
        });
      }
    },

    switchUserEvent(state) {
      sendGTMEvent({
        event: 'tv-switch_user',
      });
    },

    TotpDoLaterEvent(state) {
      sendGTMEvent({
        event: 'tv-totp_do_later',
      });
    },

    setNewUser(state, action: PayloadAction<boolean>) {
      state.isNewUser = action.payload;
    },

    setOTPVerified(state) {
      state.isOTPVerified = true;
    },

    // New action to refresh current user from localStorage
    refreshCurrentUser(state) {
      try {
        if (typeof window === 'undefined') return;

        const storedUser = safeParseJSON<LoginInfo | null>(STORAGE_KEY, null);
        const authToken = localStorage.getItem('dock_universal_token');
        const storedSessionTime = safeParseJSON<number>(MOMENT_KEY, 0);

        if (storedUser && authToken && isValidLoginInfo(storedUser)) {
          const validation = validateUserData(storedUser);

          if (validation.isValid) {
            state.user = storedUser;
            state.isLoggedIn = true;
            state.email = storedUser.email || null;
            state.mobile = storedUser.mobile || null;
            state.user_id = storedUser.user_id || null;
            state.sessionTime = storedSessionTime;

            // Check token validity
            checkTokenValid(state);
          } else {
            console.warn('Stored user data validation failed:', validation.errors);
            // Invalid user data, logout
            state.isLoggedIn = false;
            state.user = null;
            state.email = null;
            state.mobile = null;
            state.user_id = null;
          }
        } else {
          // No valid auth found, logout
          state.isLoggedIn = false;
          state.user = null;
          state.email = null;
          state.mobile = null;
          state.user_id = null;
        }
      } catch (error) {
        console.error('Error refreshing current user:', error);
        state.isLoggedIn = false;
        state.user = null;
        state.error_message = 'Failed to refresh user data';
      }
    },

    // New action to update user data partially
    updateUser(state, action: PayloadAction<Partial<LoginInfo>>) {
      if (state.user) {
        const updatedUser = { ...state.user, ...action.payload };

        // Validate updated data
        const validation = validateUserData(updatedUser);
        if (validation.isValid) {
          state.user = updatedUser;
          persistUserData(updatedUser);
        } else {
          console.warn('Updated user data validation failed:', validation.errors);
          state.error_message = 'Invalid user data update';
        }
      }
    },
  },
});

// ==================== SELECTORS ====================

// Basic selectors
export const currentUser = (state: { auth: AuthState }): LoginInfo | null => state.auth.user;
export const isAuthenticated = (state: { auth: AuthState }): boolean => state.auth.isLoggedIn;
export const isNewUser = (state: { auth: AuthState }): boolean => state.auth.isNewUser;
export const isOTPVerified = (state: { auth: AuthState }): boolean => state.auth.isOTPVerified;
export const getAuthStatus = (state: { auth: AuthState }): string => state.auth.status;
export const getAuthError = (state: { auth: AuthState }): string | null => state.auth.error_message;

// User data selectors
export const getCurrentAuthToken = (state: { auth: AuthState }): string | undefined =>
  state.auth.user?.access_token || state.auth.user?.auth_token;
export const getUserId = (state: { auth: AuthState }): string | null => state.auth.user_id;
export const getUserName = (state: { auth: AuthState }): string | null => state.auth.user?.user_name || null;
export const getUserEmail = (state: { auth: AuthState }): string | null => state.auth.email;
export const getUserMobile = (state: { auth: AuthState }): string | null => state.auth.mobile;

// Session and token selectors
export const getSessionTime = (state: { auth: AuthState }): number => state.auth.sessionTime;
export const isTokenExpired = (state: { auth: AuthState }): boolean => state.auth.tokenExp.ssoToken;
export const getTokenExpState = (state: { auth: AuthState }): TokenExp => state.auth.tokenExp;

// Permission selectors
export const isTradingActive = (state: { auth: AuthState }): boolean =>
  state.auth.user ? hasUserPermission(state.auth.user, 'trading') : false;
export const isDematActive = (state: { auth: AuthState }): boolean =>
  state.auth.user ? hasUserPermission(state.auth.user, 'demat') : false;
export const isMFActive = (state: { auth: AuthState }): boolean =>
  state.auth.user ? hasUserPermission(state.auth.user, 'mf') : false;
export const isMpinEnabled = (state: { auth: AuthState }): boolean =>
  state.auth.user ? hasUserPermission(state.auth.user, 'mpin') : false;

// Theme and UI selectors
export const getThemeData = (state: { auth: AuthState }): any => state.auth.themeData;
export const getThemeDataBG = (state: { auth: AuthState }): any => state.auth.theamDataBG;

// Data selectors
export const getRefreshData = (state: { auth: AuthState }): RefreshData | any | null => state.auth.data;

// ==================== EXPORTS ====================

export const {
  setUser,
  setStatus,
  setMessage,
  clearMessage,
  setThemeData,
  setThemeDataBG,
  setEmailAndMobile,
  setOtpData,
  setSessionTime,
  logout,
  loginEvent,
  setMpinEnable,
  initialCheckData,
  switchUserEvent,
  TotpDoLaterEvent,
  setQrData,
  updateAuthTokenForSession,
  setNewUser,
  setOTPVerified,
  refreshCurrentUser,
  updateUser,
} = authReducer.actions;

export default authReducer.reducer;